import { __, _n, sprintf } from '@wordpress/i18n';
import { apiFetch } from './api';

/**
 * Read the staff review table: for each row, the chosen side and the
 * (possibly edited) proposed value.
 *
 * @param {HTMLElement} review `.anacrm-suggestion-review` wrapper.
 * @return {Object} field key → { choice, value }
 */
function collectSuggestionChoices( review ) {
	const fields = {};
	review.querySelectorAll( 'tr[data-field]' ).forEach( ( row ) => {
		const choice = row.querySelector( '.anacrm-suggestion-choice:checked' );
		const input = row.querySelector( '.anacrm-suggestion-input' );
		let value = null;
		if ( input && input.tagName === 'SELECT' && input.multiple ) {
			value = [ ...input.selectedOptions ].map( ( o ) => o.value );
		} else if ( input && input.dataset.kind === 'pick' ) {
			value = input.value ? [ input.value ] : [];
		} else if ( input ) {
			value = input.value;
		}
		fields[ row.dataset.field ] = {
			choice: choice ? choice.value : '',
			value,
		};
	} );
	return fields;
}

/**
 * Enable "Save" only once every row has a side chosen; editing a proposed
 * value selects "Proposed" for that row.
 *
 * @param {HTMLElement} review `.anacrm-suggestion-review` wrapper.
 */
function updateSuggestionReview( review ) {
	const rows = [ ...review.querySelectorAll( 'tr[data-field]' ) ];
	const missing = rows.filter(
		( row ) => ! row.querySelector( '.anacrm-suggestion-choice:checked' )
	).length;
	review
		.querySelectorAll(
			'.anacrm-suggestion-save, .anacrm-claim-review-btn:not([data-action="reject"])'
		)
		.forEach( ( btn ) => {
			btn.disabled = missing > 0;
		} );
	review.querySelector( '.anacrm-suggestion-pending' ).textContent = missing
		? sprintf(
				/* translators: %d: number of fields still to review */
				_n(
					'%d field still to review',
					'%d fields still to review',
					missing,
					'anacrm'
				),
				missing
		  )
		: '';
}

[ 'change', 'input' ].forEach( ( type ) =>
	document.addEventListener( type, ( e ) => {
		const review = e.target.closest?.( '.anacrm-suggestion-review' );
		if ( ! review ) {
			return;
		}
		if ( e.target.matches( '.anacrm-suggestion-input' ) ) {
			const radio = e.target
				.closest( 'tr' )
				.querySelector( '.anacrm-suggestion-choice[value="proposed"]' );
			radio.checked = true;
		}
		updateSuggestionReview( review );
	} )
);

document.addEventListener( 'DOMContentLoaded', () => {
	const $ = window.jQuery;
	document
		.querySelectorAll( '.anacrm-suggestion-review' )
		.forEach( ( review ) => {
			// Searchable chip-style picks, like the Pods org form (SelectWoo).
			if ( $?.fn?.selectWoo ) {
				review
					.querySelectorAll( '.anacrm-suggestion-input[data-kind="pick"]' )
					.forEach( ( select ) => {
						const single = ! select.multiple;
						$( select )
							.selectWoo( {
								width: '100%',
								allowClear: single,
								placeholder: single ? ' ' : undefined,
							} )
							// select2 fires jQuery events only; re-emit natively.
							.on( 'change', ( e ) => {
								// jQuery also sees the native event we dispatch: skip it.
								if ( e.originalEvent ) {
									return;
								}
								select.dispatchEvent(
									new Event( 'change', { bubbles: true } )
								);
							} );
					} );
			}
			updateSuggestionReview( review );
		} );
} );

/**
 * Org reclaim workflow buttons on the org single:
 * - `.anacrm-reclaim-btn`: create (or reopen) the user's claim and go to it.
 * - `.anacrm-claim-review-btn`: staff approve / reject a pending claim.
 * - `.anacrm-suggest-btn`: create (or reopen) the user's suggestion and go to it.
 * - `.anacrm-suggestion-save`: staff save the field-by-field review of a suggestion.
 */
document.addEventListener( 'click', async ( e ) => {
	const reclaimBtn = e.target.closest( '.anacrm-reclaim-btn' );
	if ( reclaimBtn ) {
		reclaimBtn.disabled = true;
		try {
			const res = await apiFetch(
				`/anacrm/v1/org/${ reclaimBtn.dataset.orgId }/reclaim`,
				{ method: 'POST' }
			);
			window.location.href = res.url;
		} catch ( err ) {
			reclaimBtn.disabled = false;
			window.alert(
				__( 'Could not reclaim the organisation:', 'anacrm' ) +
					' ' +
					err.message
			);
		}
		return;
	}

	const suggestBtn = e.target.closest( '.anacrm-suggest-btn' );
	if ( suggestBtn ) {
		suggestBtn.disabled = true;
		try {
			const res = await apiFetch(
				`/anacrm/v1/org/${ suggestBtn.dataset.orgId }/suggest`,
				{ method: 'POST' }
			);
			window.location.href = res.url;
		} catch ( err ) {
			suggestBtn.disabled = false;
			window.alert(
				__( 'Could not start the suggestion:', 'anacrm' ) +
					' ' +
					err.message
			);
		}
		return;
	}

	const saveBtn = e.target.closest( '.anacrm-suggestion-save' );
	if ( saveBtn ) {
		const review = saveBtn.closest( '.anacrm-suggestion-review' );
		saveBtn.disabled = true;
		try {
			const res = await apiFetch(
				`/anacrm/v1/org/${ review.dataset.suggestionId }/suggestion`,
				{
					method: 'POST',
					body: JSON.stringify( {
						fields: collectSuggestionChoices( review ),
					} ),
				}
			);
			window.location.href = res.url;
		} catch ( err ) {
			saveBtn.disabled = false;
			window.alert(
				__( 'Could not save the new version:', 'anacrm' ) +
					' ' +
					err.message
			);
		}
		return;
	}

	const reviewBtn = e.target.closest( '.anacrm-claim-review-btn' );
	if ( ! reviewBtn ) {
		return;
	}
	const { claimId, action } = reviewBtn.dataset;
	const isApproval = action !== 'reject';
	let message = __( 'Reject and discard this version?', 'anacrm' );
	if ( action === 'approve' ) {
		message = __(
			'Approve this version? It will replace the published data; the owner of the org will not change.',
			'anacrm'
		);
	} else if ( action === 'approve_owner' ) {
		message = __(
			'Approve this version? It will replace the published data and the claimant will become the owner of the org.',
			'anacrm'
		);
	}
	if ( ! window.confirm( message ) ) {
		return;
	}
	reviewBtn.disabled = true;
	const review = reviewBtn.closest( '.anacrm-reclaim-review' );
	try {
		const res = await apiFetch( `/anacrm/v1/org/${ claimId }/claim`, {
			method: 'PATCH',
			body: JSON.stringify( {
				action,
				...( review && isApproval
					? { fields: collectSuggestionChoices( review ) }
					: {} ),
			} ),
		} );
		window.location.href = res.url;
	} catch ( err ) {
		reviewBtn.disabled = false;
		window.alert(
			__( 'Could not update the claim:', 'anacrm' ) + ' ' + err.message
		);
	}
} );
